import { useState } from "react"
import type { AutomaticValueRecoveryStatus } from "@nova-ai/shared"
import {
  useAutomaticValueRecovery,
  useRecoveryChanges,
} from "../../services/use-automatic-value-recovery"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { Button } from "../ui/button"
import { Table } from "../ui/table"

export function AutomaticRecoveryProgress({
  marketplace,
  runId,
}: {
  marketplace: string
  runId: number
}) {
  const query = useAutomaticValueRecovery(marketplace, runId)
  if (query.isPending) {
    return <p role="status">Loading automatic recovery…</p>
  }
  if (query.error) {
    return <p role="alert">Automatic recovery status unavailable: {query.error.message}</p>
  }
  return <AutomaticRecoverySummary status={query.data} />
}

function AutomaticRecoverySummary({ status }: { status: AutomaticValueRecoveryStatus }) {
  const { controls, attempt } = status
  const hasException = attempt?.status === "exception" || attempt?.status === "failed"
  const isUpdating =
    controls.replacementEnabled && !hasException && attempt && attempt.status !== "complete"
  return (
    <div className="space-y-2" role="status" aria-live="polite">
      <h3 className="font-medium">{recoveryHeading(status)}</h3>

      <RecoveryCauses causes={status.causes ?? []} />

      {isUpdating && <p>Updating marketplace values.</p>}

      {hasException && <p className="text-warning">{attempt?.exception}</p>}

      {!controls.replacementEnabled && (
        <p className="text-muted-foreground">
          Manual recovery remains available. Enabling automation needs scoped authorization.
        </p>
      )}

      <RecoveryValidationOutcome status={status} />

      {attempt && (
        <details>
          <summary className="cursor-pointer text-muted-foreground">Recovery diagnostics</summary>

          <p className="mt-2">
            {`${humanizeEnumLabel(attempt.stage)} · ${humanizeEnumLabel(attempt.status)}`}
          </p>
        </details>
      )}
    </div>
  )
}
function recoveryHeading(status: AutomaticValueRecoveryStatus) {
  if (status.attempt?.status === "exception" || status.attempt?.status === "failed") {
    return "A recovery decision is needed"
  }
  return status.controls.replacementEnabled
    ? "Automatic value recovery is enabled"
    : "Automatic value recovery is paused"
}
function validationMessage(status: AutomaticValueRecoveryStatus) {
  if (status.validation?.status === "complete") {
    return "This run received a fresh whole-run check."
  }
  return status.controls.validationEnabled
    ? "A fresh whole-run check will follow value convergence."
    : "Automatic run checks are paused. Run Check after values become current."
}
function RecoveryValidationOutcome({ status }: { status: AutomaticValueRecoveryStatus }) {
  const validation = status.validation
  return (
    <>
      <p>{validationMessage(status)}</p>

      {validation?.status === "complete" && validation.result?.buildBlocked === true && (
        <p className="text-warning">
          Values recovered. Independent listing gaps still need attention.
        </p>
      )}

      {validation?.status === "failed" && (
        <p className="text-warning">
          The automatic check failed. Review diagnostics and run Check again.
        </p>
      )}
    </>
  )
}
function RecoveryCauses({ causes }: { causes: { kind: string; evidence: string }[] }) {
  return (
    <ul className="space-y-1">
      {causes.map((cause) => (
        <li key={`${cause.kind}:${cause.evidence}`}>
          {`${humanizeEnumLabel(cause.kind)}: ${cause.evidence}`}
        </li>
      ))}
    </ul>
  )
}

export function RecoveryFieldChanges({
  marketplace,
  generationId,
}: {
  marketplace: string
  generationId: number
}) {
  const [page, setPage] = useState<{ offset: number; fingerprint?: string }>({ offset: 0 })
  const query = useRecoveryChanges(marketplace, generationId, page.offset, page.fingerprint)
  const data = query.data
  return (
    <div className="space-y-3">
      <h3 className="font-medium">What changed across this marketplace</h3>

      {query.isPending && <p role="status">Loading field changes…</p>}

      {query.error && (
        <div role="alert">
          <p>{query.error.message}</p>

          <Button onClick={() => setPage({ offset: 0 })}>Reload change details</Button>
        </div>
      )}

      {data && (
        <>
          <ul className="space-y-1">
            {data.causes.map((cause) => (
              <li key={`${cause.kind}:${cause.evidence}`}>
                {`${humanizeEnumLabel(cause.kind)}. ${cause.evidence}`}
              </li>
            ))}
          </ul>

          {Boolean(data.subsequentReplay?.length) && (
            <details>
              <summary>Later source replay (after this replacement)</summary>

              <RecoveryCauses causes={data.subsequentReplay ?? []} />
            </details>
          )}

          <p className="text-muted-foreground">
            {`${data.uniqueChangedProducts.toLocaleString()} products with effective field changes · ${data.total.toLocaleString()} changed fields · ${data.totalProducts.toLocaleString()} products in the full selection · ${data.exclusions.toLocaleString()} exclusions.`}
          </p>

          <Table
            embedded
            rows={data.items}
            rowKey={(row) => `${row.configSku}:${row.simpleSku}:${row.field}`}
            columns={[
              {
                key: "product",
                header: "Product / field",
                render: (row) => (
                  <span className="break-words">
                    {row.configSku}

                    {row.simpleSku ? ` / ${row.simpleSku}` : ""}

                    <br />

                    {row.field}
                  </span>
                ),
              },
              {
                key: "previous",
                header: "Previous",
                render: (row) => (
                  <span className="whitespace-pre-wrap break-words">
                    {row.previous ?? "Absent"}
                  </span>
                ),
              },
              {
                key: "next",
                header: "Replacement",
                render: (row) => (
                  <span className="whitespace-pre-wrap break-words">{row.next ?? "Absent"}</span>
                ),
              },
              {
                key: "source",
                header: "Evidence",
                render: (row) => (
                  <details>
                    <summary className="cursor-pointer">
                      {row.nextSource ? humanizeEnumLabel(row.nextSource) : "Unknown"}
                    </summary>

                    <p>
                      {`Previous: ${row.previousSource ?? "unknown"}, revision ${row.previousRevisionId ?? "unknown"}. Replacement revision ${row.nextRevisionId ?? "unknown"}.`}
                    </p>
                  </details>
                ),
              },
            ]}
            empty="No effective field changes. Fresh identity and validation may still be required."
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={page.offset === 0 || query.isFetching}
              onClick={() =>
                setPage({ offset: Math.max(0, page.offset - 25), fingerprint: data.fingerprint })
              }
            >
              Previous fields
            </Button>

            <span className="text-muted-foreground">
              {`${Math.min(page.offset + 1, data.total)}–${Math.min(page.offset + 25, data.total)} of ${data.total}`}
            </span>

            <Button
              disabled={!data.pagination.hasMore || query.isFetching}
              onClick={() => setPage({ offset: page.offset + 25, fingerprint: data.fingerprint })}
            >
              Next fields
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
